/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import moment from "moment";

import { times } from "Constants/Time";

import { placeholderString } from "../Constants/ProfileConstants";

const emptyDate = moment.utc("0001-01-01T00:00:00").format();

/**
 * Checks if date provided is empty
 * @method
 * @param {Date} dateObject The date object.
 * @returns {boolean} if provided date is empty.
 */
export const isDateEmpty = (dateObject: Date): boolean => {
  return moment.utc(dateObject).format() === emptyDate;
};

/**
 * Formats the date to string.
 * @method
 * @param {Date} dateObject The date object.
 * @returns {string} The date string.
 */
export const formatDate = (dateObject: Date): string => {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const utcDateStr = moment.utc(dateObject).format();
  const momentDate = new Date(utcDateStr);

  return utcDateStr === emptyDate
    ? placeholderString
    : momentDate.toLocaleDateString("en-US", options as Intl.DateTimeFormatOptions);
};

/**
 * Formats the date to a short string.
 * @method
 * @param {Date} dateObject The date object.
 * @returns {string} The date string.
 */
export const formatDateAsShortString = (dateObject: Date): string => {
  return dateObject.getFullYear() + "-" + (dateObject.getMonth() + 1) + "-" + dateObject.getDate();
};

/**
 * Formats the date to a short string.
 * @method
 * @param {Date} dateObject The date object.
 * @returns {string} The date string.
 */
export const formatDateAsString = (dateObject: Date): string => {
  return new Date(dateObject).toDateString();
};

/**
 * Converts from date time to date.
 * @method
 * @param {Date} dateTime The date object.
 * @returns {Date} The date string.
 */
export const convertFromDateTimeToDate = (dateTime: any): Date => new Date(Date.parse(dateTime));

/**
 * Returns UTC date by local date.
 * @method
 * @param {Date} date local date.
 * @param {number} timeZoneOffset time zone offset.
 * @returns {string} UTC Date.
 */
export const getEventDateAsLocalDateString = (date: Date, timeZoneOffset: number): string => {
  let eventDate = new Date(date);
  eventDate = new Date(eventDate.setMinutes(eventDate.getMinutes() + timeZoneOffset));
  return new Date(
    eventDate.getUTCFullYear(),
    eventDate.getUTCMonth(),
    eventDate.getUTCDate()
  ).toDateString();
};

/**
 * Returns event time txt.
 * @method
 * @param {Date} date local date.
 * @param {number} timeZoneOffset time zone offset.
 * @returns {string} Event time key.
 */
export const getEventTimeText = (date: Date, timeZoneOffset: number): string => {
  let eventDate = new Date(date);
  eventDate = new Date(eventDate.setMinutes(eventDate.getMinutes() + timeZoneOffset));
  const timespan = `${String(eventDate.getUTCHours()).padStart(2, "0")}:${String(
    eventDate.getUTCMinutes()
  ).padStart(2, "0")}:00`;
  const time = times.filter((time) => time.timespan === timespan);
  return time.length > 0 ? time[0].text : times[0].text;
};

/**
 * Formats the Name.
 * @method
 * @param {string} firstName The first name.
 * @param {string} lastName The last name.
 * @returns {string} The formatted name.
 */
export const formatName = (firstName: string, lastName: string): string =>
  firstName + " " + lastName;

/**
 * Formats the Address.
 * @method
 * @param {string} address1 The first address line.
 * @param {string} address2 The second address line.
 * @param {string} addressCity The address city line.
 * @param {string} addressStateorProvince The address state/province line.
 * @param {string} addressPostCode The address post code line.
 * @param {string} addressCountryOrRegionName The address country/region line.
 * @returns {string} The formatted name.
 */
export const formatAddress = (
  address1: string,
  address2: string,
  addressCity: string,
  addressStateorProvince: string,
  addressPostCode: string,
  addressCountryOrRegionName: string
): string =>
  address1 +
  " " +
  address2 +
  " " +
  addressCity +
  " " +
  addressStateorProvince +
  " " +
  addressPostCode +
  " " +
  addressCountryOrRegionName;

/**
 * Returns a formatted date and time string.
 * @method
 * @param {Date} date The date to format.
 * @returns {string} The formatted date and time string.
 */
export const getFormattedDateTime: (date: Date) => string = (date: Date) => {
  return moment(new Date(date)).format("llll");
};

export const formatDateEu = (dateObject: Date): string => {
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  const utcDateStr = moment.utc(dateObject).format();
  const momentDate = new Date(utcDateStr);

  return utcDateStr === emptyDate
    ? placeholderString
    : momentDate.toLocaleDateString("en-GB", options as Intl.DateTimeFormatOptions);
};

/**
 * Modifies the minutes of the date to minus the time zone offset. This essentially shifts the time back to UTC.
 * It needs to be used in conjunction with {addDateTimeOffset}.
 * @method
 * @param {Date | undefined} date Date.
 * @returns {Date | undefined} Date.
 */
export const removeDateTimeOffset = (date: Date | undefined): Date | undefined => {
  if (!date) return date;
  let dt = new Date(date);
  dt = new Date(dt.setMinutes(dt.getMinutes() + dt.getTimezoneOffset()));
  return dt;
};

/**
 * Modifies the minutes of the date to add the time zone offset. This essentially shifts the time back to UTC.
 * It needs to be used in conjunction with {removeDateTimeOffset}.
 * @method
 * @param {Date | undefined} date Date.
 * @returns {Date | undefined} Date.
 */
export const addDateTimeOffset = (date: Date | undefined): Date | undefined => {
  if (!date) return date;
  let dt = new Date(date);
  dt = new Date(dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset()));
  return dt;
};
